/**
 * src/utils/titresDeRoute.ts
 *
 * Chantier J : accessibilité (WCAG 2.4.2 « Titre de page », RGAA 8.5).
 *
 * LE DÉFAUT
 *   `DocumentBranding` posait `document.title = event.name`, donc le MÊME
 *   titre sur les quelque 140 écrans de l'application. Une personne au lecteur
 *   d'écran, qui s'oriente d'abord au titre du document, entendait le nom de
 *   l'événement à chaque navigation et n'apprenait jamais où elle venait
 *   d'arriver. C'est aussi ce qu'on lit dans un onglet, un favori ou un
 *   historique : « le même partout » y équivaut à « aucun ».
 *
 * LE CHOIX : UNE TABLE, PAS 140 APPELS
 *   La voie habituelle, un `usePageTitle('…')` posé dans chaque écran, a deux
 *   défauts rédhibitoires ici : il faut modifier 140 fichiers (dont ceux que
 *   d'autres chantiers tiennent), et surtout le prochain écran écrit sera celui
 *   qu'on oubliera. Le titre redeviendrait muet sans que personne ne s'en
 *   aperçoive.
 *
 *   On dérive donc le titre de la ROUTE, à un seul endroit. Un écran n'a rien à
 *   faire pour être correctement titré ; il suffit d'inscrire sa route ici, et
 *   un test garde-fou vérifie qu'aucune route de `App.tsx` n'y manque.
 *
 *   Les segments dynamiques (`/exposants/:id`) donnent un titre de CATÉGORIE.
 *   C'est volontairement générique : le nom précis n'est connu qu'après la
 *   réponse serveur, et un titre qui change en cours de chargement est plus
 *   déroutant qu'utile. Un écran qui veut aller plus loin peut poser son propre
 *   titre ; celui-ci reste le plancher garanti.
 *
 * ⚠ AUCUN nom d'événement en dur ici : il vient de la configuration
 *   (`tne_events.name`) et c'est `DocumentBranding` qui l'assemble. Le
 *   vocabulaire métier passe par les jetons de lexique ({lexWorkshop…},
 *   {lexExhibitor…}, {lexVisitor…}) : le même écran change de nom selon le type
 *   d'événement configuré, sans une ligne de code de plus.
 *
 * Mikael - chef de projet - 2026-09-09
 */

import { matchPath } from 'react-router-dom';

/**
 * Motif de route (syntaxe React Router) → clé i18n du titre de l'écran.
 *
 * L'ORDRE COMPTE : on retient la PREMIÈRE correspondance. Les routes les plus
 * spécifiques doivent donc précéder les plus générales (`/admin/pages/:id/versions`
 * avant `/admin/pages/:id`, qui précède lui-même `/admin/pages`).
 */
export const TITRES_DE_ROUTE: ReadonlyArray<readonly [string, string]> = [
  // ── Écrans publics et espace personnel ───────────────────────────────
  ['/', 'acces.titre.accueil'],
  ['/splash', 'acces.titre.splash'],
  ['/onboarding', 'acces.titre.onboarding'],
  ['/role', 'acces.titre.role'],
  ['/inscription/:role', 'acces.titre.inscriptionRole'],
  ['/inscription', 'acces.titre.inscription'],
  ['/connexion', 'acces.titre.connexion'],
  // 2026-10-05 : accès VIP. Mikael - chef de projet - 2026-10-05
  ['/vip', 'vip.accueil.titrePage'],
  ['/vip/acces', 'vip.acces.titre'],
  ['/vip/connexion', 'vip.connexion.titre'],
  ['/inscription/vip', 'vip.acces.titreAncienLien'],
  ['/accepter-invitation', 'acces.titre.accepterInvitation'],
  ['/install-help-ios', 'acces.titre.installIos'],

  ['/decouvrir', 'acces.titre.salon'],
  ['/programme', 'acces.titre.programme'],
  ['/atelier/:slug', 'acces.titre.atelierDetail'],
  ['/exposants/:id', 'acces.titre.exposantDetail'],
  ['/exposants', 'acces.titre.exposants'],
  ['/postes/:id', 'acces.titre.posteDetail'],
  ['/postes', 'acces.titre.postes'],
  ['/carte', 'acces.titre.carte'],
  ['/kiosque', 'acces.titre.kiosque'],
  ['/borne', 'acces.titre.borne'],
  ['/partenaires', 'acces.titre.partenaires'],
  ['/partenaire/:slug', 'acces.titre.partenaireDetail'],
  ['/organisateur', 'acces.titre.organisateur'],
  ['/medias', 'acces.titre.medias'],
  ['/media/:id', 'acces.titre.mediaDetail'],
  ['/videos', 'acces.titre.videos'],
  ['/webradio', 'acces.titre.webradio'],
  ['/webtv', 'acces.titre.webtv'],
  ['/retrospective', 'acces.titre.retrospective'],
  ['/presse', 'acces.titre.presse'],
  ['/a-propos', 'acces.titre.aPropos'],
  ['/contact', 'acces.titre.contact'],
  ['/accessibilite', 'acces.titre.accessibilite'],
  ['/mentions-legales', 'acces.titre.mentionsLegales'],
  ['/confidentialite', 'acces.titre.confidentialite'],
  ['/plus', 'acces.titre.plus'],
  // 2026-10-07 : page Wi-Fi. Mikael - chef de projet - 2026-10-07
  ['/wifi', 'wifi.page.titre'],
  ['/p/:slug', 'acces.titre.pageCms'],

  ['/mon-espace', 'acces.titre.monEspace'],
  ['/mon-compte', 'acces.titre.monCompte'],
  ['/mon-badge/print', 'acces.titre.monBadgeImpression'],
  ['/mon-badge', 'acces.titre.monBadge'],
  ['/mon-guide', 'acces.titre.monGuide'],
  ['/ma-carte', 'acces.titre.maCarte'],
  ['/mon-programme', 'acces.titre.monProgramme'],
  ['/mon-parcours', 'acces.titre.monParcours'],
  ['/mon-profil', 'acces.titre.monProfil'],
  ['/mes-favoris', 'acces.titre.mesFavoris'],
  // 2026-09-20 (CV Matching, vague 3) : la cle vient de fr.entretiens, le
  // sous-catalogue du chantier. Mikael - chef de projet.
  // 2026-10-14 (chantier Platinium, lot P5) : les entretiens Platinium du
  // candidat. Avant /mes-entretiens, le plus precis d'abord. Cle dans
  // fr.platinium.entretiens. Mikael - chef de projet - 2026-10-14
  ['/mes-entretiens-platinium', 'platinium.entretiens.mes.titre'],
  ['/mes-entretiens', 'entretiens.mes.titre'],
  // 2026-10-04 : page « Mes conseils » apres le depot du CV. Mikael - chef de projet
  ['/mes-conseils', 'cvConseils.titre'],
  ['/mes-contacts', 'acces.titre.mesContacts'],
  ['/mon-groupe/liste', 'acces.titre.monGroupeListe'],
  ['/mon-groupe', 'acces.titre.monGroupe'],
  ['/sondages', 'acces.titre.sondages'],
  ['/mon-questionnaire-ia', 'acces.titre.questionnaireIa'],
  ['/notifications-prefs', 'acces.titre.notificationsPrefs'],
  ['/controle', 'acces.titre.controle'],
  ['/scanner', 'acces.titre.scanner'],

  // ── Exposant ─────────────────────────────────────────────────────────
  ['/exposant/login', 'acces.titre.exposantLogin'],
  ['/exposant/inscription', 'acces.titre.exposantInscription'],
  ['/exposant/demande-code', 'acces.titre.exposantDemandeCode'],
  ['/exposant/ma-fiche', 'acces.titre.exposantMaFiche'],
  ['/exposant/mon-stand', 'acces.titre.exposantMonStand'],
  ['/exposant/mes-ateliers', 'acces.titre.exposantMesAteliers'],
  // 2026-10-04 : les « rencontres » sont devenues les fiches d'entretien (un
  // seul nom a l'ecran). Cles dans fr.ficheEntretien. Mikael - chef de projet - 2026-10-04
  ['/exposant/mes-rencontres', 'fichesEntretien.titre'],
  ['/exposant/fiches/:ref', 'ficheEntretien.titre'],
  ['/exposant/cv-recus', 'acces.titre.exposantCvRecus'],
  ['/exposant/recruteurs', 'acces.titre.exposantRecruteurs'],
  // Ecran Premium (CV Matching, vague 2). La cle vient de fr.cvm, le
  // sous-catalogue du chantier, comme ma-journee vient de fr.journee.
  // Mikael - chef de projet - 2026-09-20
  ['/exposant/premium', 'cvm.premium.titre'],
  // Espace Platinium (chantier Platinium, 2026-10-14). Cle dans
  // fr.platinium.socle. Mikael - chef de projet - 2026-10-14
  ['/exposant/platinium', 'platinium.page.titre'],
  // Vue fusionnee « Tout » (CV Matching, vague 4). Cle dans fr.cvm.
  // Mikael - chef de projet - 2026-09-20
  ['/exposant/journal', 'journal.titre'],
  // Recapitulatif de fin de journee. La cle vient de `fr.journee`, le
  // sous-catalogue de ce chantier, et non de `fr.acces` : deux chantiers menes
  // en parallele ne doivent pas ecrire dans le meme catalogue. Le garde-fou ne
  // demande pas un prefixe, il demande une cle qui EXISTE et un titre UNIQUE.
  // Mikael - chef de projet - 2026-09-10
  ['/exposant/ma-journee', 'maJournee.title'],
  ['/exposant/scanner', 'acces.titre.exposantScanner'],

  // ── Animateur ────────────────────────────────────────────────────────
  ['/animateur/inscription', 'acces.titre.animateurInscription'],
  ['/animateur/demande-code', 'acces.titre.animateurDemandeCode'],
  ['/animateur/mes-ateliers', 'acces.titre.animateurMesAteliers'],
  ['/animateur', 'acces.titre.animateur'],

  // ── Administration ───────────────────────────────────────────────────
  ['/admin/login', 'acces.titre.adminLogin'],
  ['/admin/2fa-enroll', 'acces.titre.admin2fa'],
  ['/admin/password-reset-request', 'acces.titre.adminResetDemande'],
  ['/admin/password-reset', 'acces.titre.adminReset'],
  ['/admin/self-password', 'acces.titre.adminMotDePasse'],
  ['/admin/self-photo', 'acces.titre.adminPhoto'],
  ['/admin/badge-settings', 'acces.titre.adminBadgeSettings'],
  ['/admin/premium-settings', 'acces.titre.adminPremiumSettings'],
  // 2026-10-14 (chantier Platinium, lot P6). Mikael - chef de projet - 2026-10-14
  ['/admin/platinium', 'platinium.pilotage.titre'],
  ['/admin/interviews', 'entretiens.admin.titre'],
  ['/admin/dashboard/wall', 'acces.titre.adminMur'],
  ['/admin/presentation', 'acces.titre.adminPresentation'],
  ['/admin/dashboard', 'acces.titre.adminTableauDeBord'],
  ['/admin/pilotage', 'acces.titre.adminPilotage'],
  ['/admin/alerts', 'acces.titre.adminAlertes'],
  ['/admin/activity', 'acces.titre.adminActivite'],
  ['/admin/audit', 'acces.titre.adminAudit'],
  ['/admin/daily-report', 'acces.titre.adminBilanQuotidien'],
  // 2026-09-10 : le bilan d'impact. Distinct du bilan quotidien, qui ne
  // couvre que la journee.
  ['/admin/bilan', 'bilan.titreDocument'],
  ['/admin/registration-errors', 'acces.titre.adminErreursInscription'],
  ['/admin/user-journey', 'acces.titre.adminParcours'],
  ['/admin/emploi-analytics', 'acces.titre.adminAnalyticsEmploi'],
  ['/admin/heatmap', 'acces.titre.adminAffluence'],
  ['/admin/form-stats', 'acces.titre.adminStatsFormulaire'],
  ['/admin/form-builder', 'acces.titre.adminFormBuilder'],
  ['/admin/signups', 'acces.titre.adminInscriptions'],
  ['/admin/merge-visitors', 'acces.titre.adminFusionVisiteurs'],
  ['/admin/visitor-badges', 'acces.titre.adminBadges'],
  ['/admin/attendance-export', 'acces.titre.adminExportPresence'],
  ['/admin/workshop-attendance', 'acces.titre.adminPresenceAteliers'],
  ['/admin/workshop-hosts', 'acces.titre.adminAnimateurs'],
  ['/admin/workshops', 'acces.titre.adminAteliers'],
  ['/admin/role-programs', 'acces.titre.adminProgrammesParRole'],
  ['/admin/programs', 'acces.titre.adminProgrammes'],
  ['/admin/exhibitors-import', 'acces.titre.adminImportExposants'],
  ['/admin/exhibitor-code-requests', 'acces.titre.adminDemandesCodes'],
  ['/admin/exhibitor-codes', 'acces.titre.adminCodesExposants'],
  ['/admin/exhibitor-reports', 'acces.titre.adminBilansExposants'],
  ['/admin/exhibitors', 'acces.titre.adminExposants'],
  ['/admin/stand-positions', 'acces.titre.adminPositionsStands'],
  ['/admin/stands', 'acces.titre.adminStands'],
  ['/admin/plan-editor', 'acces.titre.adminPlanEditor'],
  ['/admin/person/:id', 'acces.titre.adminPersonne'],
  ['/admin/jobs', 'acces.titre.adminOffres'],
  ['/admin/cvs', 'acces.titre.adminCvs'],
  ['/admin/export-dossier', 'acces.titre.adminExportDossier'],
  // 2026-10-04 : « Demandez à l’événement ». Mikael - chef de projet
  ['/admin/demandez', 'acces.titre.adminDemandez'],
  ['/admin/visibilite', 'acces.titre.adminVisibilite'],
  ['/admin/groups', 'acces.titre.adminGroupes'],
  ['/admin/listes-nominatives', 'acces.titre.adminListesNominatives'],
  ['/admin/transport/:rotationId', 'acces.titre.adminTransportRotation'],
  ['/admin/transport', 'acces.titre.adminTransport'],
  ['/admin/control-stats/:typeId', 'acces.titre.adminStatsControleType'],
  ['/admin/control-stats', 'acces.titre.adminStatsControle'],
  ['/admin/control-types', 'acces.titre.adminTypesControle'],
  ['/admin/surveys', 'acces.titre.adminSondages'],
  // 2026-10-05 : jeu des cadeaux. Mikael - chef de projet
  ['/admin/cadeaux/verifier/:code', 'acces.titre.adminCadeauxVerifier'],
  ['/admin/cadeaux', 'acces.titre.adminCadeaux'],
  ['/admin/guides', 'acces.titre.adminGuides'],
  ['/admin/medias', 'acces.titre.adminMedias'],
  ['/admin/media-streams', 'acces.titre.adminFluxMedias'],
  ['/admin/pages/:id/versions', 'acces.titre.adminVersionsPage'],
  ['/admin/pages/:id', 'acces.titre.adminEditionPage'],
  ['/admin/pages', 'acces.titre.adminPages'],
  ['/admin/content', 'acces.titre.adminContenus'],
  ['/admin/home-tiles', 'acces.titre.adminTuilesAccueil'],
  ['/admin/navigation', 'acces.titre.adminNavigation'],
  ['/admin/partners', 'acces.titre.adminPartenaires'],
  ['/admin/organisateurs', 'acces.titre.adminOrganisateurs'],
  ['/admin/tags', 'acces.titre.adminTags'],
  ['/admin/translations', 'acces.titre.adminTraductions'],
  ['/admin/email-templates', 'acces.titre.adminModelesEmail'],
  ['/admin/email-composer', 'acces.titre.adminComposeurEmail'],
  ['/admin/email-2fa', 'acces.titre.adminEmail2fa'],
  ['/admin/app-2fa', 'acces.titre.adminApp2fa'],
  ['/admin/securite-comptes', 'acces.titre.adminSecuriteComptes'],
  ['/admin/securite', 'acces.titre.adminSecurite'],
  ['/admin/vip-invitations', 'acces.titre.adminInvitationsVip'],
  ['/admin/invitations', 'acces.titre.adminInvitations'],
  ['/admin/push', 'acces.titre.adminPush'],
  ['/admin/users-admin', 'acces.titre.adminComptesAdmin'],
  ['/admin/users', 'acces.titre.adminComptes'],
  ['/admin/roles', 'acces.titre.adminRoles'],
  ['/admin/access', 'acces.titre.adminAcces'],
  ['/admin/feature-flags', 'acces.titre.adminFonctions'],
  ['/admin/event-config', 'acces.titre.adminConfigEvenement'],
  ['/admin/settings', 'acces.titre.adminReglages'],
  ['/admin/maintenance', 'acces.titre.adminMaintenance'],
  ['/admin/deploy-tokens', 'acces.titre.adminJetonsDeploiement'],
  ['/admin/webhooks', 'acces.titre.adminWebhooks'],
  ['/admin/kiosk-launchers', 'acces.titre.adminBornes'],
  ['/admin/osnum-status', 'acces.titre.adminOsnum'],
  ['/admin/ai-config', 'acces.titre.adminIa'],
  ['/admin', 'acces.titre.admin'],
];

/** Clé i18n du titre servi quand aucune route ne correspond (404). */
export const CLE_TITRE_INTROUVABLE = 'acces.titre.introuvable';

/**
 * Clé i18n du titre de l'écran correspondant à `pathname`.
 * Retourne `CLE_TITRE_INTROUVABLE` si aucun motif ne correspond : c'est bien
 * ce qu'on veut afficher : l'écran rendu est alors la page « introuvable ».
 */
export function cleDeTitrePour(pathname: string): string {
  for (const [motif, cle] of TITRES_DE_ROUTE) {
    if (matchPath({ path: motif, end: true }, pathname) !== null) return cle;
  }
  return CLE_TITRE_INTROUVABLE;
}
